import React, { useState, useEffect } from 'react';
import { 
  Search, 
  Bell, 
  Check, 
  Clock, 
  User, 
  Stethoscope, 
  UserCheck, 
  AlertCircle,
  FileText,
  Settings,
  ArrowRight
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
  CommandSeparator,
} from '@/components/ui/command';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { notificationsService, type Notification } from '@/services/notifications';
import { formatDistanceToNow } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '@/hooks/useAuth';
import { getDefaultRouteByRole, isAdminRole } from '@/lib/roleRouting';

export function GlobalSearch() {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  const { user } = useAuth();
  const admin = isAdminRole(user?.role);

  useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };
    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const handleSelect = (path: string) => {
    setOpen(false);
    navigate(path);
  };

  return (
    <>
      <div 
        onClick={() => setOpen(true)}
        className="relative hidden md:flex items-center w-72 h-10 px-3 py-2 bg-gray-50 dark:bg-neutral-800/50 border border-gray-200 dark:border-neutral-700 rounded-lg cursor-pointer hover:border-teal-500/50 hover:bg-white dark:hover:bg-neutral-800 transition-all group"
      >
        <Search className="w-4 h-4 text-gray-400 group-hover:text-teal-500 transition-colors" />
        <span className="ml-2 text-sm text-gray-500 dark:text-gray-400 group-hover:text-gray-700 dark:group-hover:text-gray-200">
          Buscar médicos, pacientes...
        </span>
        <kbd className="ml-auto pointer-events-none inline-flex h-5 select-none items-center gap-1 rounded border border-gray-200 dark:border-neutral-700 bg-white dark:bg-neutral-900 px-1.5 font-mono text-[10px] font-medium text-gray-400 opacity-100">
          <span className="text-xs">⌘</span>K
        </kbd>
      </div>

      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput placeholder="O que você está procurando?" />
        <CommandList className="max-h-[450px]">
          <CommandEmpty>Nenhum resultado encontrado.</CommandEmpty>
          
          {admin && (
            <>
              <CommandGroup heading="Médicos">
                <CommandItem onSelect={() => handleSelect('/admin/aprovacao-medicos')}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-teal-100 dark:bg-teal-900/30 flex items-center justify-center">
                      <Stethoscope className="h-4 w-4 text-teal-600 dark:text-teal-400" />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">Dr. Roberto Santos</span>
                      <span className="text-[10px] text-gray-500">Cardiologista • CRM 12345</span>
                    </div>
                  </div>
                  <Badge className="ml-auto" variant="outline">Pendente</Badge>
                </CommandItem>
                <CommandItem onSelect={() => handleSelect('/admin')}>
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center">
                      <Stethoscope className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                    </div>
                    <div className="flex flex-col">
                      <span className="font-medium text-sm">Dra. Julia Mendes</span>
                      <span className="text-[10px] text-gray-500">Pediatra • CRM 67890</span>
                    </div>
                  </div>
                  <Badge className="ml-auto bg-green-50 text-green-700 border-green-200 dark:bg-green-900/20 dark:text-green-400 dark:border-green-800">Ativo</Badge>
                </CommandItem>
              </CommandGroup>
              
              <CommandSeparator />
            </>
          )}
          
          <CommandGroup heading="Pacientes">
            <CommandItem onSelect={() => handleSelect(admin ? '/admin?tab=patients' : '/pacientes')}>
              <div className="flex items-center gap-2">
                <div className="w-8 h-8 rounded-full bg-gray-100 dark:bg-neutral-800 flex items-center justify-center">
                  <User className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </div>
                <div className="flex flex-col">
                  <span className="font-medium text-sm">Carlos Eduardo</span>
                  <span className="text-[10px] text-gray-500">Última consulta: 12/03/2024</span>
                </div>
              </div>
              <span className="ml-auto text-[10px] text-muted-foreground uppercase font-semibold">Paciente</span>
            </CommandItem>
          </CommandGroup>
          
          <CommandSeparator />
          
          <CommandGroup heading="Atalhos e Ações">
            {admin && (
              <CommandItem onSelect={() => handleSelect('/admin/aprovacao-medicos')}>
                <UserCheck className="mr-2 h-4 w-4 text-amber-500" />
                <span>Aprovações Pendentes</span>
              </CommandItem>
            )}
            <CommandItem onSelect={() => handleSelect('/settings')}>
              <Settings className="mr-2 h-4 w-4 text-gray-500" />
              <span>Configurações do Sistema</span>
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}

export function NotificationCenter() {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [hasOpened, setHasOpened] = useState(false);
  const [loading, setLoading] = useState(true);
  const unreadCount = Array.isArray(notifications) ? notifications.filter(n => !n.isRead).length : 0;
  const navigate = useNavigate();
  const { user } = useAuth();
  const admin = isAdminRole(user?.role);
  const homeRoute = getDefaultRouteByRole(user?.role);

  const fetchNotifications = async () => {
    try {
      const data = await notificationsService.getAll();
      setNotifications(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao buscar notificações:', error);
      setNotifications([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchNotifications();
    
    // Polling a cada 30 segundos
    const interval = setInterval(fetchNotifications, 30000);
    return () => clearInterval(interval);
  }, []);

  const handleOpenChange = (open: boolean) => {
    if (open) {
      setHasOpened(true);
    }
  };

  const handleNotificationClick = async (notification: Notification) => {
    // Marcar como lida no backend se ainda não estiver lida
    if (!notification.isRead) {
      try {
        await notificationsService.markAsRead(notification.id);
        // Atualizar estado local
        setNotifications(prev => 
          prev.map(n => n.id === notification.id ? { ...n, isRead: true } : n)
        );
      } catch (error) {
        console.error('Erro ao marcar notificação como lida:', error);
      }
    }
    
    // Navegar se houver link
    if (notification.link) {
      // Não navegar DOCTOR/PATIENT para rotas admin mesmo se o link vier do backend
      if (notification.link.startsWith('/admin') && !admin) {
        navigate(homeRoute);
        return;
      }
      navigate(notification.link);
    } else {
      // Fallback de navegação baseado no título
      if (admin && notification.title.includes('Médico')) {
        navigate('/admin/aprovacao-medicos');
      } else if (notification.title.includes('Consulta')) {
        navigate(homeRoute);
      } else if (notification.title.includes('Relatório')) {
        navigate(homeRoute);
      } else {
        navigate(homeRoute);
      }
    }
  };

  const markAllAsRead = async () => {
    try {
      await notificationsService.markAllAsRead();
      setNotifications(prev => prev.map(n => ({ ...n, isRead: true })));
    } catch (error) {
      console.error('Erro ao marcar todas as notificações como lidas:', error);
    }
  };

  const getIcon = (type: Notification['type']) => {
    switch (type) {
      case 'warning': return <AlertCircle className="w-4 h-4" />;
      case 'success': return <Check className="w-4 h-4" />;
      case 'error': return <XIcon className="w-4 h-4" />;
      default: return <Clock className="w-4 h-4" />;
    }
  };

  const getTypeStyles = (type: Notification['type']) => {
    switch (type) {
      case 'warning': return "bg-amber-100 text-amber-600 dark:bg-amber-900/30 dark:text-amber-400";
      case 'success': return "bg-green-100 text-green-600 dark:bg-green-900/30 dark:text-green-400";
      case 'error': return "bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400";
      default: return "bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400";
    }
  };

  return (
    <Popover onOpenChange={handleOpenChange}>
      <PopoverTrigger className="relative p-2.5 hover:bg-gray-100 dark:hover:bg-neutral-800 rounded-lg transition-all group outline-none">
        <Bell className={cn(
          "w-5 h-5 transition-colors",
          unreadCount > 0 && !hasOpened ? "text-teal-600 dark:text-teal-400" : "text-gray-600 dark:text-gray-400"
        )} />
        {unreadCount > 0 && !hasOpened && (
          <span className="absolute top-2 right-2 w-4 h-4 bg-red-500 text-white text-[10px] flex items-center justify-center rounded-full font-bold border-2 border-white dark:border-neutral-900 animate-pulse">
            {unreadCount}
          </span>
        )}
      </PopoverTrigger>
      <PopoverContent className="w-[380px] p-0" align="end">
        <div className="flex items-center justify-between p-4 border-b border-gray-100 dark:border-neutral-800">
          <div>
            <h4 className="font-bold text-sm text-gray-900 dark:text-white">Central de Notificações</h4>
            <p className="text-[11px] text-gray-500">Você tem {unreadCount} notificações não lidas</p>
          </div>
          {unreadCount > 0 && (
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={markAllAsRead}
              className="text-[11px] h-7 px-2 text-teal-600 hover:text-teal-700 hover:bg-teal-50 dark:text-teal-400 dark:hover:bg-teal-900/20"
            >
              Limpar tudo
            </Button>
          )}
        </div>
        
        <ScrollArea className="h-[400px]">
          {loading ? (
            <div className="h-[300px] flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-teal-500"></div>
            </div>
          ) : (notifications && notifications.length > 0) ? (
            <div className="divide-y divide-gray-50 dark:divide-neutral-900">
              {notifications.map((notification) => (
                <div 
                  key={notification.id}
                  onClick={() => handleNotificationClick(notification)}
                  className={cn(
                    "p-4 cursor-pointer hover:bg-gray-50 dark:hover:bg-neutral-800/50 transition-all flex gap-3 relative group/item",
                    !notification.isRead && "bg-teal-50/30 dark:bg-teal-900/5"
                  )}
                >
                  {/* Indicador visual de notificação individual não lida */}
                  {!notification.isRead && (
                    <div className="absolute left-0 top-0 bottom-0 w-1 bg-teal-500" />
                  )}
                  
                  <div className={cn(
                    "w-10 h-10 rounded-full flex items-center justify-center shrink-0 shadow-sm",
                    getTypeStyles(notification.type)
                  )}>
                    {getIcon(notification.type)}
                  </div>
                  
                  <div className="flex-1 space-y-1 pr-2">
                    <div className="flex items-center justify-between">
                      <p className={cn(
                        "text-xs font-bold leading-none",
                        !notification.isRead ? "text-gray-900 dark:text-white" : "text-gray-500 dark:text-gray-400"
                      )}>
                        {notification.title}
                      </p>
                      <span className="text-[9px] text-gray-400 font-medium whitespace-nowrap">
                        {formatDistanceToNow(new Date(notification.createdAt), { addSuffix: true, locale: ptBR })}
                      </span>
                    </div>
                    <p className="text-[11px] text-gray-500 dark:text-gray-400 leading-relaxed line-clamp-2">
                      {notification.description}
                    </p>
                    
                    {notification.link && (
                      <div className="flex items-center gap-1 text-[10px] text-teal-600 dark:text-teal-400 font-semibold pt-1 opacity-0 group-hover/item:opacity-100 transition-opacity">
                        Ver detalhes <ArrowRight className="w-3 h-3" />
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="h-[300px] flex flex-col items-center justify-center p-8 text-center">
              <div className="w-16 h-16 bg-gray-50 dark:bg-neutral-800 rounded-full flex items-center justify-center mb-4">
                <Bell className="w-8 h-8 text-gray-300 dark:text-neutral-700" />
              </div>
              <h5 className="font-semibold text-sm text-gray-900 dark:text-white">Tudo limpo por aqui!</h5>
              <p className="text-xs text-gray-500 mt-1 max-w-[200px]">
                Você não tem nenhuma nova notificação no momento.
              </p>
            </div>
          )}
        </ScrollArea>
        
        <div className="p-3 border-t border-gray-100 dark:border-neutral-800 bg-gray-50/50 dark:bg-neutral-900/50">
          <Button 
            variant="outline" 
            className="w-full text-xs h-9 font-semibold border-gray-200 dark:border-neutral-700" 
            onClick={() => {
              setHasOpened(true);
              navigate('/notifications');
            }}
          >
            Ver histórico completo
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}

const XIcon = ({ className }: { className?: string }) => (
  <svg 
    xmlns="http://www.w3.org/2000/svg" 
    width="24" 
    height="24" 
    viewBox="0 0 24 24" 
    fill="none" 
    stroke="currentColor" 
    strokeWidth="2" 
    strokeLinecap="round" 
    strokeLinejoin="round" 
    className={className}
  >
    <path d="M18 6 6 18"/><path d="m6 6 12 12"/>
  </svg>
);
